package com.gloopgo.game.domain

import com.gloopgo.game.engine.GameConstants

/**
 * Lightweight snapshot of a connected network player for staleness and elder calculation.
 */
data class NetworkPlayerSnapshot(
    val id: String,
    val joinedAt: Long,
    val lastSeen: Long
)

/**
 * Determines the room Elder responsible for zombie player pruning and mass garbage collection.
 *
 * Rules:
 * 1. The player with the oldest `joinedAt` timestamp among all active (non-stale) players is the Elder.
 * 2. Deterministic tie-break: lower `uid` lexicographical order wins.
 * 3. A player is stale / zombie if `(currentTime - lastSeen) > timeoutMs` (default: 5000ms).
 */
object ElderDetermination {

    fun isLocalPlayerElder(
        localPlayerId: String,
        allPlayers: List<NetworkPlayerSnapshot>,
        currentTime: Long,
        timeoutMs: Long = GameConstants.HEARTBEAT_TIMEOUT_MS
    ): Boolean {
        val elder = findElder(allPlayers, currentTime, timeoutMs)
        return elder?.id == localPlayerId
    }

    fun findElder(
        allPlayers: List<NetworkPlayerSnapshot>,
        currentTime: Long,
        timeoutMs: Long = GameConstants.HEARTBEAT_TIMEOUT_MS
    ): NetworkPlayerSnapshot? {
        val activePlayers = allPlayers.filter { player ->
            (currentTime - player.lastSeen) <= timeoutMs
        }

        return activePlayers.minWithOrNull(
            compareBy<NetworkPlayerSnapshot> { it.joinedAt }
                .thenBy { it.id }
        )
    }

    /**
     * Returns the IDs of all players who have not sent updates for > timeoutMs.
     */
    fun findStalePlayers(
        allPlayers: List<NetworkPlayerSnapshot>,
        currentTime: Long,
        timeoutMs: Long = GameConstants.HEARTBEAT_TIMEOUT_MS
    ): List<String> {
        return allPlayers
            .filter { (currentTime - it.lastSeen) > timeoutMs }
            .map { it.id }
    }
}
